/**
 * Horizon's slideshow arrows wrap around on an [infinite] slideshow
 * (select() maps out-of-range indexes to the other end), but drag/swipe
 * resolves through `clamp(next + modifier, 0, slides.length - 1)` in
 * assets/slideshow.js — so a swipe on the last slide toward "next" (or the
 * first slide toward "previous") dead-ends where the arrow would loop.
 *
 * Delegated document listeners instead of patching the native component:
 * when a horizontal swipe starts AND ends on a boundary slide of an
 * [infinite] slideshow — the one gesture the native handler has nothing to
 * scroll for — request the same wrap the arrow would.
 *
 * Boundary is read from the scroller's own scrollLeft/scrollWidth, not the
 * component's atStart/atEnd getters — those are index-based
 * (current === slides.length - 1), which only reaches the last slide when
 * exactly one slide is visible at a time. A+ rows can show multiple columns
 * at once, where the deepest scroll position lands on
 * slides.length - visibleCount, so atEnd never becomes true and only the
 * atStart-side wrap (always reachable) ever fired.
 *
 * Capture phase — Horizon's drag handler pointer-captures the component and
 * calls stopImmediatePropagation on pointermove; pointerdown/up still reach
 * the document in capture.
 */
(function () {
  if (/** @type {any} */ (window)._atombergSlideshowInfiniteDragInit) return;
  /** @type {any} */ (window)._atombergSlideshowInfiniteDragInit = true;

  var SWIPE_THRESHOLD_PX = 30;
  // A settled scroll position can sit a few px short of the true
  // scrollWidth-clientWidth max on galleries with a slide gap (e.g. the PDP
  // media gallery's image_gap) — observed 8px there. 1px was tight enough to
  // miss that, silently dead-ending the wrap. Full-bleed sliders (no gap)
  // still land within this tolerance of the real boundary either way.
  var BOUNDARY_TOLERANCE_PX = 24;

  /**
   * @param {Element} component
   * @returns {{atStart: boolean, atEnd: boolean} | null}
   */
  function scrollBoundary(component) {
    var scroller = component.querySelector('slideshow-slides');
    if (!scroller) return null;
    var max = scroller.scrollWidth - scroller.clientWidth;
    return {
      atStart: scroller.scrollLeft <= BOUNDARY_TOLERANCE_PX,
      atEnd: max <= 0 || scroller.scrollLeft >= max - BOUNDARY_TOLERANCE_PX,
    };
  }

  /** @type {{component: any, x: number, y: number, atStart: boolean, atEnd: boolean} | null} */
  var gesture = null;

  document.addEventListener(
    'pointerdown',
    function (e) {
      gesture = null;
      if (!(e.target instanceof Element)) return;
      var component = /** @type {any} */ (e.target.closest('slideshow-component[infinite]'));
      if (!component) return;
      var boundary = scrollBoundary(component);
      if (!boundary) return;
      gesture = {
        component: component,
        x: e.clientX,
        y: e.clientY,
        atStart: boundary.atStart,
        atEnd: boundary.atEnd,
      };
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    function () {
      gesture = null;
    },
    true
  );

  document.addEventListener(
    'pointerup',
    function (e) {
      if (!gesture) return;
      var g = gesture;
      gesture = null;

      var dx = e.clientX - g.x;
      var dy = e.clientY - g.y;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

      var wantsNext = dx < 0 && g.atEnd;
      var wantsPrevious = dx > 0 && g.atStart;
      if (!wantsNext && !wantsPrevious) return;

      // setTimeout 0: let Horizon's own pointerup snap-back finish first —
      // select() awaits its in-flight scroll and can no-op if called mid-snap.
      setTimeout(function () {
        var component = g.component;
        // Re-check the boundary after the native handler settled: if the swipe
        // actually moved off the boundary slide, there is nothing to wrap.
        var boundary = scrollBoundary(component);
        if (!boundary) return;
        if (wantsNext && boundary.atEnd) {
          if (typeof component.next === 'function') component.next();
        } else if (wantsPrevious && boundary.atStart) {
          if (typeof component.previous === 'function') component.previous();
        }
      }, 0);
    },
    true
  );
})();
